(() => {
  const cfg = window.pangoDeliveryConfig || {};

  // Normalizar festivos: acepta array o string JSON
  let holidays = cfg.holidays;
  if (typeof holidays === 'string') {
    try { holidays = JSON.parse(holidays); } catch { holidays = []; }
  }
  if (!Array.isArray(holidays)) holidays = [];
  holidays = holidays.map(String);

  const excludeDaysOfWeek = cfg.excludeDaysOfWeek || [0, 6];
  const cutOffTime = cfg.cutOffTime || '23:59';

  const ymdFormatter = new Intl.DateTimeFormat('fr-CA', {
    year: 'numeric', month: '2-digit', day: '2-digit'
  });

  // Devuelve un Date con la fecha de entrega
  const calculateDeliveryDate = (ndays, now = new Date()) => {
    const [h, m] = cutOffTime.split(':').map(Number);
    const cutOffDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0, 0);
    const n = ndays + (now > cutOffDate ? 1 : 0);

    const deliveryOn = new Date(now);
    deliveryOn.setHours(0, 0, 0, 0);

    let i = 0;
    while (true) {
      const yyyymmdd = ymdFormatter.format(deliveryOn);
      const canDeliver =
        !excludeDaysOfWeek.includes(deliveryOn.getDay()) &&
        !holidays.some(pattern => yyyymmdd.endsWith(pattern));

      if (canDeliver && i++ === n) break;
      deliveryOn.setDate(deliveryOn.getDate() + 1);
    }
    return deliveryOn;
  };

  /**
   * Fecha de recepción formateada ("Sep 10")
   * @param {'internal'|'external'} type
   */
  window.getPangoDeliveryDate = (type = 'internal', now = new Date()) => {
    const raw = type === 'external' ? cfg.externalDays : cfg.internalDays;
    const days = parseInt(raw, 10);
    if (Number.isNaN(days)) return null;

    return calculateDeliveryDate(days, now)
        .toLocaleDateString(cfg.locale || 'en-US', { month: 'short', day: 'numeric' })
        .replace(',', '');
    };

  // ---- Stock de Pango (peticiones agrupadas + caché) ----
  const STOCK_API = 'https://jarana-external-providers.cap.pangostudio.com/api/stocks';
  const MAX_IDS_PER_REQUEST = 50; // evita URLs demasiado largas
  const stockCache = new Map();   // id -> Promise<{internal, external} | null>

  const isInternalLocation = (name = '') =>
    name.includes('DisfracesJarana') || name.includes('FYASA');

  const fetchStockChunk = async (ids) => {
    const res = await fetch(`${STOCK_API}?id=${ids.join(',')}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    const result = new Map(ids.map(id => [id, { internal: 0, external: 0 }]));
    data.forEach(product => {
      const acc = result.get(String(product.id));
      if (!acc) return;
      (product.locations || []).forEach(loc => {
        if (isInternalLocation(loc.locationName)) acc.internal += loc.quantity;
        else acc.external += loc.quantity;
      });
    });
    //console.log("Hago llamada al servidor PANGO")
    return result;
  };

  // Devuelve Map(id -> {internal, external} | null) con una llamada por bloque de ids
  window.getPangoStocks = async (ids) => {
    const unique = [...new Set(ids.filter(Boolean).map(String))];
    const pending = unique.filter(id => !stockCache.has(id));

    for (let i = 0; i < pending.length; i += MAX_IDS_PER_REQUEST) {
      const chunk = pending.slice(i, i + MAX_IDS_PER_REQUEST);
      const request = fetchStockChunk(chunk).catch(err => {
        console.error('PANGO stock error', err);
        chunk.forEach(id => stockCache.delete(id)); // permite reintentar más tarde
        return null;
      });
      chunk.forEach(id => stockCache.set(id, request.then(map => map?.get(id) ?? null)));
    }

    const values = await Promise.all(unique.map(id => stockCache.get(id)));
    return new Map(unique.map((id, i) => [id, values[i]]));
  };

  // Se mantiene por compatibilidad, pero ya pasa por la caché
  window.getPangoExternalStocks = async (id) =>
    (await window.getPangoStocks([id])).get(String(id)) ?? null;

  // Rellena los wrappers de product card / cart line con UNA sola petición
  window.initPangoEnvios = async (root = document) => {
    const wrappers = [...root.querySelectorAll('[data-pango-variant]:not([data-pango-loaded])')];
    if (!wrappers.length) return;

    // Marcar antes del await para que el observer no los vuelva a coger
    wrappers.forEach(w => (w.dataset.pangoLoaded = 'true'));

    const stocks = await window.getPangoStocks(wrappers.map(w => w.dataset.pangoVariant));

    wrappers.forEach(wrapper => {
      const stock = stocks.get(String(wrapper.dataset.pangoVariant));
      if (!stock) return;

      const quantity = parseInt(wrapper.dataset.pangoQuantity, 10) || 1;
      let type = null;
      if (stock.internal >= quantity) type = 'internal';
      else if (stock.external > 0) type = 'external';
      if (!type) return;

      const span = wrapper.querySelector(`.pango_${type}_stock`);
      if (!span) return;

      const dateEl = span.querySelector('[data-pango-date]');
      if (dateEl) dateEl.textContent = window.getPangoDeliveryDate(type);
      span.classList.add('is-visible');
    });
  };

  // Observa el DOM y procesa los wrappers que se inyecten después de la carga
  const startPangoObserver = () => {
    let scheduled = false;

    const observer = new MutationObserver((mutations) => {
      const hasNewWrapper = mutations.some(m =>
        [...m.addedNodes].some(node =>
          node.nodeType === 1 &&
          (node.matches?.('[data-pango-variant]') || node.querySelector?.('[data-pango-variant]'))
        )
      );
      if (!hasNewWrapper || scheduled) return;

      // Agrupa varias mutaciones seguidas en una sola pasada
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        window.initPangoEnvios();
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  document.addEventListener('DOMContentLoaded', () => {
    window.initPangoEnvios();
    startPangoObserver();
  });
})();

// Añade fecha de envío a página cart
(() => {
  const getBadges = () => document.querySelectorAll('[data-pango-cart-delivery] [data-pango-type]');

  const showMessage = (type, date) => {
    getBadges().forEach(badge => {
      const isTarget = badge.dataset.pangoType === type;
      badge.hidden = !isTarget;
      if (isTarget) badge.querySelector('[data-pango-date]').textContent = date;
    });
  };

  const hideAll = () => getBadges().forEach(badge => (badge.hidden = true));

  const getInternalQty = (fetchedItem) =>
    fetchedItem.locations.reduce((sum, loc) => {
      const name = loc.locationName;
      return name.includes('DisfracesJarana') || name.includes('FYASA')
        ? sum + loc.quantity
        : sum;
    }, 0);

  window.updatePangoCartDelivery = async () => {
    if (!getBadges().length) return;
    try {
      const cart = await (await fetch('/cart.js')).json();
      const cartItems = cart.items || [];
      if (!cartItems.length) { hideAll(); return; }

      const ids = cartItems.map(i => i.variant_id).join(',');
      const res = await fetch(`https://jarana-external-providers.cap.pangostudio.com/api/stocks?id=${ids}`);
      const fetched = await res.json();

      let hasInternal = false;
      let hasExternal = false;

      cartItems.forEach(cartItem => {
        const match = fetched.find(f => Number(f.id) === Number(cartItem.variant_id));
        const internalQty = match ? getInternalQty(match) : 0;
        if (cartItem.quantity > internalQty) hasExternal = true;
        else hasInternal = true;
      });

      let type;
      if (hasInternal && hasExternal) type = 'mixed';
      else if (hasExternal) type = 'external';
      else type = 'internal';

      const date = getPangoDeliveryDate(type === 'internal' ? 'internal' : 'external');
      if (!date) { hideAll(); return; }

      showMessage(type, date);
    } catch (err) {
      console.error('PANGO cart delivery error', err);
      hideAll();
    }
  };

  // Observa el body para detectar el re-render del carrito (el contenedor
  // observado no puede ser el que se reemplaza, o el observer se pierde)
  const observeCart = () => {
    let timer = null;
    const observer = new MutationObserver((mutations) => {
      const touched = mutations.some(m =>
        [...m.addedNodes].some(n =>
          n.nodeType === 1 &&
          (n.matches?.('[data-pango-cart-delivery]') || n.querySelector?.('[data-pango-cart-delivery]'))
        )
      );
      if (!touched) return;
      clearTimeout(timer);
      timer = setTimeout(() => window.updatePangoCartDelivery(), 300);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const init = () => {
    window.updatePangoCartDelivery();
    observeCart();

    // Dawn publica este evento tras actualizar cantidades / eliminar líneas
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        setTimeout(() => window.updatePangoCartDelivery(), 300);
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();